import {
  createContext,
  useContext,
  useState,
  useEffect,
  ReactNode,
  useCallback,
} from 'react';
import axios from 'axios';

interface User {
  id: string;
  email: string;
  name: string;
  role: 'admin' | 'manager' | 'production';
  store: {
    id: string;
    name: string;
    shopifyDomain: string;
  };
}

interface AuthContextType {
  user: User | null;
  loading: boolean;
  isEmbedded: boolean;
  login: (email: string, password: string) => Promise<void>;
  logout: () => Promise<void>;
  refreshToken: () => Promise<void>;
  authenticateWithSessionToken: (sessionToken: string) => Promise<void>;
}

const AuthContext = createContext<AuthContextType | null>(null);

const api = axios.create({
  baseURL: '/api',
});

// Token management
function getAccessToken(): string | null {
  return localStorage.getItem('accessToken');
}

function getRefreshToken(): string | null {
  return localStorage.getItem('refreshToken');
}

function setTokens(accessToken: string, refreshToken: string): void {
  localStorage.setItem('accessToken', accessToken);
  localStorage.setItem('refreshToken', refreshToken);
}

function clearTokens(): void {
  localStorage.removeItem('accessToken');
  localStorage.removeItem('refreshToken');
}

// Set up axios interceptors
api.interceptors.request.use((config) => {
  const token = getAccessToken();
  if (token) {
    config.headers.Authorization = `Bearer ${token}`;
  }
  return config;
});

api.interceptors.response.use(
  (response) => response,
  async (error) => {
    const originalRequest = error.config;

    if (error.response?.status === 401 && !originalRequest._retry) {
      originalRequest._retry = true;

      const refreshToken = getRefreshToken();
      if (refreshToken) {
        try {
          const response = await axios.post('/api/auth/refresh', {
            refreshToken,
          });

          const { accessToken, refreshToken: newRefreshToken } = response.data;
          setTokens(accessToken, newRefreshToken);

          originalRequest.headers.Authorization = `Bearer ${accessToken}`;
          return api(originalRequest);
        } catch {
          clearTokens();
          window.location.href = '/login';
        }
      }
    }

    return Promise.reject(error);
  }
);

// Detect embedded mode
function isEmbeddedApp(): boolean {
  const urlParams = new URLSearchParams(window.location.search);
  return !!urlParams.get('host');
}

export function AuthProvider({ children }: { children: ReactNode }) {
  const [user, setUser] = useState<User | null>(null);
  const [loading, setLoading] = useState(true);
  const isEmbedded = isEmbeddedApp();

  // Authenticate using Shopify session token (for embedded apps)
  const authenticateWithSessionToken = useCallback(async (sessionToken: string) => {
    try {
      const response = await api.post('/shopify/session', { sessionToken });
      const { accessToken, refreshToken, user: userData, store } = response.data;

      setTokens(accessToken, refreshToken);
      setUser({
        id: userData.id,
        email: userData.email,
        name: userData.name,
        role: userData.role,
        store: {
          id: store.id,
          name: store.name,
          shopifyDomain: store.shopifyDomain,
        },
      });
    } catch (error) {
      console.error('Session token authentication failed:', error);
      throw error;
    }
  }, []);

  useEffect(() => {
    const initAuth = async () => {
      // In embedded mode, wait for ShopifyProvider to provide token
      // The authentication will be triggered by the parent component
      if (isEmbedded) {
        setLoading(false);
        return;
      }

      // Standalone mode: use stored JWT tokens
      const token = getAccessToken();
      if (token) {
        try {
          const response = await api.get('/auth/me');
          setUser(response.data.user);
        } catch {
          clearTokens();
        }
      }
      setLoading(false);
    };

    initAuth();
  }, [isEmbedded]);

  const login = async (email: string, password: string) => {
    const response = await api.post('/auth/login', { email, password });
    const { accessToken, refreshToken } = response.data;

    setTokens(accessToken, refreshToken);

    // Fetch user data
    const userResponse = await api.get('/auth/me');
    setUser(userResponse.data.user);
  };

  const logout = async () => {
    // In embedded mode, we don't really "logout" - just clear state
    if (isEmbedded) {
      clearTokens();
      setUser(null);
      return;
    }

    const refreshToken = getRefreshToken();
    try {
      await api.post('/auth/logout', { refreshToken });
    } catch {
      // Ignore errors during logout
    }

    clearTokens();
    setUser(null);
    window.location.href = '/login';
  };

  const refreshToken = async () => {
    const token = getRefreshToken();
    if (!token) throw new Error('No refresh token');

    const response = await api.post('/auth/refresh', { refreshToken: token });
    const { accessToken, refreshToken: newRefreshToken } = response.data;

    setTokens(accessToken, newRefreshToken);
  };

  return (
    <AuthContext.Provider
      value={{ user, loading, isEmbedded, login, logout, refreshToken, authenticateWithSessionToken }}
    >
      {children}
    </AuthContext.Provider>
  );
}

export function useAuth() {
  const context = useContext(AuthContext);
  if (!context) {
    throw new Error('useAuth must be used within an AuthProvider');
  }
  return context;
}

export { api };
